import { OnboardingConversationArea } from '@/components/onboarding/OnboardingConversationArea';
import { TrainingFeedback, FeedbackData } from '@/components/onboarding/TrainingFeedback';
import {
  CallState,
  LoadingState,
  useOnboardingAgentCall,
} from '@/hooks/useOnboardingAgentCall';
import { useCoachStore } from '@/store/coach';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import { useState } from 'react';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/utils/ky';

// Jago Coffee training content for sidebar
const JAGO_COFFEE_CONTENT = [
  {
    id: 'about',
    title: 'TENTANG JAGO COFFEE',
    items: [
      'Sejarah dan Misi Perusahaan',
      'Layanan Tanpa Ongkir',
      'Area Layanan dan Lokasi',
      'Aplikasi Mobile Jago Coffee',
      'Target 100+ Lokasi di Jakarta',
      'Komitmen Kualitas Kopi Premium',
    ],
  },
  {
    id: 'grooming',
    title: 'GROOMING',
    items: [
      'Standar Rambut Pendek dan Rapi',
      'Kebersihan Kuku dan Tangan',
      'Penggunaan Seragam Lengkap',
      'Kebersihan Badan dan Wajah',
      'Larangan Aksesoris Berlebihan',
      'Penampilan Profesional Mobile Barista',
    ],
  },
  {
    id: 'products',
    title: 'PRODUK',
    items: [
      'Menu Kopi Susu Jago (Signature)',
      'Produk dengan Kopi Robusta',
      'Produk dengan Kopi Arabica',
      'Citrus Coldbrew dan Varian Dingin',
      'Aturan Penyimpanan Suhu Produk',
      'Kualitas dan Expired Date',
    ],
  },
  {
    id: 'cart',
    title: 'PENGENALAN CART',
    items: [
      'Jarak Tempuh Battery (35km)',
      'Cara Penggunaan Dashboard Cart',
      'Prosedur Charging Battery',
      'Maintenance dan Perawatan Harian',
      'Sistem Keamanan dan Safety',
      'Pelaporan Kerusakan ke Jagoan 101',
    ],
  },
];

export function OnboardingAgent() {
  const coachStore = useCoachStore();
  const [hasStartedCall, setHasStartedCall] = useState(false);
  const [selectedContent, setSelectedContent] = useState<
    (typeof JAGO_COFFEE_CONTENT)[0] | null
  >(null);
  const [searchParams] = useSearchParams();
  const [feedbackData, setFeedbackData] = useState<FeedbackData | null>(null);
  const [isGeneratingFeedback, setIsGeneratingFeedback] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // Get company and persona from URL params
  const company = searchParams.get('company');
  const persona =
    company === 'jago'
      ? 'jago-trainer'
      : searchParams.get('persona') || 'female';

  const {
    loadingState,
    error,
    callState,
    sessionId,
    agentInfo,
    callDuration,
    transcript,
    isSpeaking,
    handleAnswerCall,
    handleEndCall,
    formatTime,
    handleSendMessage,
  } = useOnboardingAgentCall({
    userId: coachStore.userId,
    persona,
    onCallEnded: () => {
      // Start generating feedback when call ends
      generateFeedback();
    },
  });

  // Function to generate feedback from backend
  const generateFeedback = async () => {
    try {
      setIsGeneratingFeedback(true);
      
      // Prepare transcript data with proper timestamp format
      // Map "agent" to "ai" as per backend update
      const formattedTranscript = transcript.map(entry => ({
        source: entry.source === 'agent' ? 'ai' : entry.source,
        message: entry.message,
        timestamp: entry.timestamp.toISOString(),
      }));
      
      // Call the actual API endpoint
      console.log('Sending feedback request:', {
        transcript: formattedTranscript.length + ' messages',
        userId: coachStore.userId || 'anonymous',
        callDuration,
        sessionId: sessionId || undefined,
      });

      const response = await api.post('api/onboarding/feedback', {
        json: {
          transcript: formattedTranscript,
          userId: coachStore.userId || 'anonymous',
          callDuration,
          sessionId: sessionId || undefined,
        },
      });
      
      const result = await response.json() as 
        | { success: true; data: FeedbackData }
        | { ok: false; error: { type: string; code: string; message: string; details?: string } };
      
      console.log('API Response data:', result);
      
      // Handle both success and error response formats
      if (response.ok && 'success' in result && result.success) {
        // Success response format
        setFeedbackData(result.data);
        setShowFeedback(true);
      } else if ('ok' in result && !result.ok) {
        // Error response format
        throw new Error(result.error?.message || 'Failed to generate feedback');
      } else {
        // Unexpected response format
        throw new Error('Unexpected response format from server');
      }
    } catch (error) {
      console.error('Error generating feedback:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        transcriptLength: transcript.length,
        userId: coachStore.userId,
        callDuration,
      });
      
      // Show error toast notification with more specific message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Gagal menganalisis hasil training: ${errorMessage}`);
      
      // Fallback: Show basic completion message without detailed feedback
      // The UI will show the simple completion screen instead of feedback
    } finally {
      setIsGeneratingFeedback(false);
    }
  };

  const handleStartCall = () => {
    setHasStartedCall(true);
    // Use setTimeout to ensure state updates are processed first
    setTimeout(() => {
      handleAnswerCall();
    }, 100);
  };

  const handleNewCall = () => {
    window.location.reload();
  };

  // Base layout with sidebar and main area
  const renderWithSidebar = (mainContent: React.ReactNode) => (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f5f5f5',
      }}
    >
      {/* Header with logos - always visible */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
          bgcolor: 'white',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        {/* Jago Coffee Logo */}
        <img
          src="https://dopmo1eihgbgm.cloudfront.net/68b81d7b27352545f9c422d2/jago.png"
          alt="Jago Coffee"
          style={{
            height: '40px',
            objectFit: 'contain',
          }}
        />

        {/* Powered by Hupo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: '#A0522D', fontSize: '12px' }}
          >
            Powered by
          </Typography>
          <img
            src="/logos/Hupo_Logotype_Orange(noR).svg"
            alt="Hupo"
            style={{
              height: '18px',
              marginTop: '1px',
            }}
          />
        </Box>
      </Box>

      {/* Main content area */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar with Jago Coffee content */}
        <Box
          sx={{
            width: 320,
            bgcolor: 'white',
            borderRight: '1px solid #e0e0e0',
            p: 3,
            overflowY: 'auto',
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 3, fontWeight: 600, color: '#D2691E' }}
          >
            Jago Coffee Training
          </Typography>
          {JAGO_COFFEE_CONTENT.map((section) => (
            <Box
              key={section.id}
              onClick={() => setSelectedContent(section)}
              sx={{
                mb: 3,
                p: 2,
                bgcolor: '#FFF8DC',
                borderRadius: 2,
                cursor: 'pointer',
                border: '1px solid #DEB887',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: '#F5DEB3',
                  transform: 'translateX(4px)',
                  borderColor: '#CD853F',
                },
              }}
            >
              <Box
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  fontSize: '14px',
                  color: '#8B4513',
                }}
              >
                {section.title}
              </Box>
              <Box sx={{ fontSize: '12px', color: '#A0522D' }}>
                {section.items.length} items to review
              </Box>
            </Box>
          ))}
        </Box>

        {/* Main area */}
        {mainContent}
      </Box>
    </Box>
  );

  // Welcome screen - before call
  if (callState === CallState.INCOMING && !hasStartedCall) {
    // Only show Jago content if company=jago
    if (company === 'jago') {
      return (
        <>
          {renderWithSidebar(
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
            }}
          >
            <Box
              sx={{
                textAlign: 'center',
                maxWidth: 500,
              }}
            >
              {/* Avatar */}
              <Box
                sx={{
                  width: 160,
                  height: 160,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  margin: '0 auto',
                  mb: 3,
                  boxShadow: '0 8px 24px rgba(210, 105, 30, 0.3)',
                }}
              >
                <img
                  src={agentInfo.profile.avatar}
                  alt="Jago Coffee Training"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                  }}
                />
              </Box>

              {/* Title */}
              <Typography
                variant="h4"
                sx={{ fontWeight: 600, mb: 1, color: '#8B4513' }}
              >
                Sari - Jago Coffee Trainer
              </Typography>
              <Typography variant="body1" sx={{ color: '#A0522D', mb: 4 }}>
                Training untuk Mobile Barista Jago Coffee
              </Typography>

              {/* Start Training Button */}
              <Box
                onClick={handleStartCall}
                sx={{
                  px: 6,
                  py: 3,
                  bgcolor: '#D2691E',
                  color: 'white',
                  borderRadius: 3,
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(210, 105, 30, 0.3)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: '#CD853F',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(210, 105, 30, 0.4)',
                  },
                }}
              >
                Mulai Training
              </Box>
            </Box>
          </Box>,
        )}

        {/* Content Detail Dialog */}
        <Dialog
          open={!!selectedContent}
          onClose={() => setSelectedContent(null)}
          maxWidth="md"
          fullWidth
        >
          {selectedContent && (
            <>
              <DialogTitle
                sx={{ m: 0, p: 2, bgcolor: '#FFF8DC', color: '#8B4513' }}
              >
                {selectedContent.title}
                <IconButton
                  aria-label="close"
                  onClick={() => setSelectedContent(null)}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: '#8B4513',
                  }}
                >
                  ✕
                </IconButton>
              </DialogTitle>
              <DialogContent dividers sx={{ bgcolor: '#FFFEF7' }}>
                {selectedContent.items.map((item, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ color: '#5D4037' }}>
                      • {item}
                    </Typography>
                  </Box>
                ))}
              </DialogContent>
            </>
          )}
        </Dialog>
      </>
    );
    }
    
    // For non-Jago companies, show nothing
    return null;
  }

  // Connecting screen
  if (callState === CallState.INCOMING && hasStartedCall) {
    if (company !== 'jago') return null;
    return renderWithSidebar(
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              overflow: 'hidden',
              margin: '0 auto',
              mb: 3,
            }}
          >
            <img
              src={agentInfo.profile.avatar}
              alt="Jago Coffee Training"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center top',
              }}
            />
          </Box>
          <Typography variant="h6" sx={{ mb: 2, color: '#8B4513' }}>
            Menghubungkan ke Sari...
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            {[0, 0.5, 1].map((delay) => (
              <Box
                key={delay}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#D2691E',
                  animation: 'pulse 1.5s infinite',
                  animationDelay: `${delay}s`,
                  '@keyframes pulse': {
                    '0%, 60%': { opacity: 0.3 },
                    '30%': { opacity: 1 },
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>,
    );
  }

  // Active call screen
  if (callState === CallState.ACTIVE) {
    if (company !== 'jago') return null;
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f5f5f5',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid #e0e0e0',
            p: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              maxWidth: 1400,
              margin: '0 auto',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={agentInfo.profile.avatar}
                  alt="Jago Coffee Training"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                  }}
                />
              </Box>
              <Box>
                <Box sx={{ fontWeight: 600, color: '#8B4513' }}>
                  Sari - Jago Coffee Training
                </Box>
                <Box sx={{ fontSize: '14px', color: '#A0522D' }}>
                  Training aktif {formatTime(callDuration)}
                </Box>
              </Box>
            </Box>

            <Box
              onClick={handleEndCall}
              sx={{
                px: 3,
                py: 1.5,
                bgcolor: '#D2691E',
                color: 'white',
                borderRadius: 2,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: '#CD853F',
                },
              }}
            >
              Selesai Training
            </Box>
          </Box>
        </Box>

        {/* Main content with sidebar */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Training content sidebar */}
          <Box
            sx={{
              width: 320,
              bgcolor: 'white',
              borderRight: '1px solid #e0e0e0',
              p: 3,
              overflowY: 'auto',
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 3, fontWeight: 600, color: '#D2691E' }}
            >
              Materi Training
            </Typography>
            {JAGO_COFFEE_CONTENT.map((section) => (
              <Box
                key={section.id}
                onClick={() => setSelectedContent(section)}
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: '#FFF8DC',
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: '1px solid #DEB887',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: '#F5DEB3',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Box
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                    fontSize: '14px',
                    color: '#8B4513',
                  }}
                >
                  {section.title}
                </Box>
                <Box sx={{ fontSize: '12px', color: '#A0522D' }}>
                  {section.items.length} items
                </Box>
              </Box>
            ))}
          </Box>

          {/* Conversation area */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <OnboardingConversationArea
              transcript={transcript}
              isSpeaking={isSpeaking}
              agentInfo={agentInfo}
              onSendMessage={handleSendMessage}
            />
          </Box>
        </Box>

        {/* Content Detail Dialog */}
        <Dialog
          open={!!selectedContent}
          onClose={() => setSelectedContent(null)}
          maxWidth="md"
          fullWidth
        >
          {selectedContent && (
            <>
              <DialogTitle
                sx={{ m: 0, p: 2, bgcolor: '#FFF8DC', color: '#8B4513' }}
              >
                {selectedContent.title}
                <IconButton
                  aria-label="close"
                  onClick={() => setSelectedContent(null)}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: '#8B4513',
                  }}
                >
                  ✕
                </IconButton>
              </DialogTitle>
              <DialogContent dividers sx={{ bgcolor: '#FFFEF7' }}>
                {selectedContent.items.map((item, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ color: '#5D4037' }}>
                      • {item}
                    </Typography>
                  </Box>
                ))}
              </DialogContent>
            </>
          )}
        </Dialog>
      </Box>
    );
  }

  // Call ended screen - show feedback
  if (callState === CallState.ENDED) {
    if (company !== 'jago') return null;
    
    // Show feedback component if available, otherwise show feedback generation or error
    if (showFeedback && feedbackData) {
      return (
        <TrainingFeedback
          feedbackData={feedbackData}
          isLoading={false}
          onNewTraining={handleNewCall}
        />
      );
    }
    
    // Show loading state while generating feedback
    if (isGeneratingFeedback) {
      return (
        <TrainingFeedback
          feedbackData={undefined}
          isLoading={true}
          onNewTraining={handleNewCall}
        />
      );
    }
    
    // Fallback: simple completion screen (in case feedback generation fails)
    return renderWithSidebar(
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box sx={{ fontSize: '48px', mb: 3 }}>✓</Box>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, mb: 1, color: '#8B4513' }}
        >
          Training Selesai!
        </Typography>
        <Typography variant="body1" sx={{ color: '#A0522D', mb: 4 }}>
          Waktu training: {formatTime(callDuration)}
        </Typography>

        <Box sx={{ textAlign: 'center' }}>
          <Box
            onClick={handleNewCall}
            sx={{
              px: 4,
              py: 2,
              bgcolor: '#D2691E',
              color: 'white',
              borderRadius: 2,
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              mb: 2,
              '&:hover': {
                bgcolor: '#CD853F',
              },
            }}
          >
            Ulangi Training
          </Box>

          <Typography variant="caption" sx={{ color: '#A0522D' }}>
            Terima kasih telah mengikuti training!
          </Typography>
        </Box>
      </Box>,
    );
  }

  // Error screen
  if (error && loadingState === LoadingState.ERROR) {
    if (company !== 'jago') return null;
    return renderWithSidebar(
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box sx={{ fontSize: '48px', mb: 3 }}>⚠️</Box>
        <Typography variant="h6" sx={{ color: '#D2691E', mb: 3 }}>
          Terjadi kesalahan sistem
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: '#A0522D', mb: 3, textAlign: 'center' }}
        >
          Maaf, terjadi masalah teknis. Silakan coba lagi.
        </Typography>
        <Box
          onClick={handleNewCall}
          sx={{
            px: 4,
            py: 2,
            bgcolor: '#D2691E',
            color: 'white',
            borderRadius: 2,
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 600,
            '&:hover': {
              bgcolor: '#CD853F',
            },
          }}
        >
          Coba Lagi
        </Box>
      </Box>,
    );
  }

  return null;
}
